import React, { FC, Fragment, useEffect, useState } from 'react';
import { Modal, Button, Tabs, Tab, Spinner } from 'react-bootstrap';

import { useDispatch } from 'react-redux'; // Import useDispatch
import { toast } from 'react-toastify';
import { PlatformDataType } from 'src/interfaces/Platform.interface';
import { uploadAsync } from 'src/redux/actions/uploadAction';
import { useAppDispatch } from 'src/redux/storeHooks';
import { accessSessionTokenKeyName } from 'src/utils/constants';
import { downloadSampleFile } from 'src/utils/downloadSampleFile';
import { isListingWarehouseUploadPlatform } from 'src/utils/listingProductUploadPlatforms';

const CSVUploadModal :FC<{ show: boolean, handleClose: () => void, id: string, platformData:PlatformDataType|null }> = ({ show, handleClose, id, platformData }) => {
    // Component logic
    const [csvFile, setCSVFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false)
    const [filetype, setFiletype] = useState('IMPORT_PRODUCTS')
    const [sampleDownloading, setSampleDownloading] = useState(false)
    const [invalidFileType, setInvalidFileType] = useState(false)
    const dispatch = useAppDispatch(); // Initialize useDispatch
    const isAmazonVendor = platformData?.name === 'AMAZONVENDOR'
    const isWayfair = platformData?.name === 'WAYFAIR'
    const hasWarehouseUpload = isListingWarehouseUploadPlatform(platformData?.name)
    const uploadTitleType = hasWarehouseUpload
        ? 'Product & Warehouse'
        : ['IMPORT_PRODUCTS', 'IMPORT_PRODUCTS_PO'].includes(filetype)
            ? 'Products'
            : filetype === 'IMPORT_SHIPPING_PROVIDER'
                ? 'Shipping Method'
                : 'Warehouses'

    useEffect(()=>{
        if(['SHIPHERO'].includes(platformData?.name!)) setFiletype('IMPORT_SHIPPING_PROVIDER')
    },[platformData?.name])
     
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]; // Access the first file from the FileList
        if (selectedFile) {
        const allowedExtensions =
          filetype === 'IMPORT_PRODUCTS_PO'
            ? ['csv', 'xlsx', 'xlsm']
            : ['csv', 'xls', 'xlsx', 'tab'];
        const fileName = selectedFile.name;
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            setInvalidFileType(true)
            setLoading(false)
            event.target.value = ''; 
            return;
        }
        setCSVFile(selectedFile);
        setInvalidFileType(false)
        } else {
            console.error("No file selected.");
        }
    };


    const handleUploadClick = async () => {
        if (csvFile instanceof File) {
            setLoading(true)
            const result = await  dispatch(uploadAsync({ id: id, csvFile, type:filetype }))
            console.log('result.payload', result.payload)
            if(result.payload?.success){
                toast.success(result.payload?.success)
                setCSVFile(null)
                handleClose()
                setLoading(false)
                return
            }
            
            if(result.payload?.data?.error){
                toast.warning(result.payload?.data?.error)
                setLoading(false)
                return
            }
            
            // Display success message using react-toastify

        }else{
            toast.warning('Please choose a file to upload')
        }
        
    };


    return (
        <Fragment>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                      Upload CSV of {uploadTitleType} from {platformData?.display_name ?? '' }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <Tabs
                    defaultActiveKey={['SHIPHERO'].includes(platformData?.name!) ? "IMPORT_SHIPPING_PROVIDER" : "IMPORT_PRODUCTS"}
                    transition={false}
                    className="mb-3"
                    onSelect={(ev)=>{
                        // console.log('event name', ev);
                        if(ev){
                            setFiletype(ev)
                            setCSVFile(null)
                        }
                    }}
                    >
                    { 
                    !['SHIPHERO'].includes(platformData?.name!) && (
                    <Tab eventKey="IMPORT_PRODUCTS" title={isAmazonVendor ? "Upload Direct Fulfilment Products" : "Upload Products list"}>
                        <span >Please go to your <small className='fs-6 fw-semibold' >{platformData?.display_name ?? '' }</small> Seller Portal & download the Product list from there to upload it here.{<button className='btn btn-link btn-sm' disabled={sampleDownloading} onClick={()=>downloadSampleFile(`${platformData?.name.toLowerCase()}_products`,setSampleDownloading,localStorage.getItem(accessSessionTokenKeyName),platformData?.name!)} ><i className="bi bi-cloud-arrow-down text-primary"></i> Download { sampleDownloading ? <Spinner animation='border' className='fs-10' size='sm' /> : null } </button>} </span>
                    </Tab> )
                    }
                    {
                    isAmazonVendor && (
                    <Tab eventKey="IMPORT_PRODUCTS_PO" title="Upload Retail Products">
                        <span >Please go to your <small className='fs-6 fw-semibold' >{platformData?.display_name ?? '' }</small> Seller Portal & download the Product list from there to upload it here.{<button className='btn btn-link btn-sm' disabled={sampleDownloading} onClick={()=>downloadSampleFile(`${platformData?.name.toLowerCase()}_products`,setSampleDownloading,localStorage.getItem(accessSessionTokenKeyName),platformData?.name!)} ><i className="bi bi-cloud-arrow-down text-primary"></i> Download { sampleDownloading ? <Spinner animation='border' className='fs-10' size='sm' /> : null } </button>} </span>
                    </Tab> )
                    }
                     { 
                    ['SHIPHERO'].includes(platformData?.name!) && (
                    <Tab eventKey="IMPORT_SHIPPING_PROVIDER" title="Upload Shipping Method List">
                        <span >Please go to your <small className='fs-6 fw-semibold' >{platformData?.display_name ?? '' }</small> Seller Portal & download the Shipping Method list from there to upload it here. Here is the sample csv for shipping method. {['SHIPHERO'].includes(platformData?.name!) && <button className='btn btn-link btn-sm' disabled={sampleDownloading} onClick={()=>downloadSampleFile(`${platformData?.name.toLowerCase()}_shipping_method`,setSampleDownloading,localStorage.getItem(accessSessionTokenKeyName),platformData?.name!)} ><i className="bi bi-cloud-arrow-down text-primary"></i> Download { sampleDownloading ? <Spinner animation='border' className='fs-10' size='sm' /> : null } </button>} </span>
                    </Tab> )
                    }
                    {
                        isListingWarehouseUploadPlatform(platformData?.name) && (
                            <Tab eventKey="IMPORT_LOCATION" title="Upload Warehouse list">
                                <span >Please go to your <small className='fs-6 fw-semibold' >{platformData?.display_name ?? '' }</small> Seller Portal & download the Warehouse list from there to upload it here. Here is the sample csv for warehouse. <button className='btn btn-link btn-sm' disabled={sampleDownloading} onClick={()=>downloadSampleFile(`${platformData?.name.toLowerCase()}_warehouses`,setSampleDownloading,localStorage.getItem(accessSessionTokenKeyName))} ><i className="bi bi-cloud-arrow-down text-primary"></i> Download { sampleDownloading ? <Spinner animation='border' className='fs-10' size='sm' /> : null } </button> </span>
                            </Tab>
                        ) 
                    }
                </Tabs>
                    <br/>
                    {/* Input field for uploading  file */}
                    <input
                      key={filetype+'change'}
                      type="file"
                      className='form-control'
                      accept={filetype === 'IMPORT_PRODUCTS_PO' ? '.csv, .xlsx, .xlsm' : '.csv, .xls, .xlsx, .tab'}
                      onChange={handleFileChange}
                    />
                    <small className={`form-text ${invalidFileType ? 'text-danger' : 'text-muted'}`} style={{marginLeft: '7rem'}}>
                      {filetype === 'IMPORT_PRODUCTS_PO'
                        ? 'Please upload a file in .csv, .xlsx or .xlsm format.'
                        : isWayfair
                          ? 'Please upload a CSV file.'
                          : 'Please upload a file in .csv, .xls, .xlsx or .tab format.'}
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button disabled={loading} variant='secondary' onClick={()=>{
                        setInvalidFileType(false)
                        setCSVFile(null) 
                        handleClose()}}>
                        Close
                    </Button>
                    <Button disabled={loading||csvFile==null} variant='primary' onClick={handleUploadClick}>
                        {loading ? 'Uploading...' : 'Upload'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default CSVUploadModal;






// import React, { FC, Fragment, useState } from 'react';
// import { Modal, Button } from 'react-bootstrap';

// import { useDispatch } from 'react-redux'; // Import useDispatch
// import { uploadAsync } from 'src/redux/actions/uploadAction';
// import { useAppDispatch } from 'src/redux/storeHooks';



// const CSVUploadModal :FC<{ show: boolean, handleClose: () => void, id: string }> = ({ show, handleClose, id }) => {
//     // Component logic
//     const [csvFile, setCSVFile] = useState<File | null>(null);
//     const [relativePath, setRelativePath] = useState<string | null>(null); // State to hold relative path
//     const dispatch = useAppDispatch(); // Initialize useDispatch

//     // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     //     const selectedFile = event.target.files?.[0]; // Access the first file from the FileList
//     //     if (selectedFile) {
//     //         setCSVFile(selectedFile);
//     //     } else {
//     //         console.error("No file selected.");
//     //     }
//     // };
//     const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//         const selectedFile = event.target.files?.[0];
//         if (selectedFile) {
//           setCSVFile(selectedFile);
      
//           // Use the library to extract relative path (example with file-saver)
//           const reader = new FileReader();
//           reader.readAsDataURL(selectedFile);
//           reader.onload = (event) => {
//             const dataURL = event.target?.result as string;
//             const blob = new Blob([dataURL], { type: selectedFile.type });
//             const path = URL.createObjectURL(blob); // Create relative path
//             setRelativePath(path); // Set relative path state
//           };
//         } else {
//           console.error("No file selected.");
//         }
//       };
      
//       const handleUploadClick = () => {
//         try {
//             if (csvFile instanceof File) {
//                 if (!relativePath) {
//                     throw new Error("Relative path not available.");
//                 }

//                 dispatch(uploadAsync({ id: id,csvFile: relativePath })); // Pass relative path to action
//                 setCSVFile(null);
//                 handleClose();
//             } else {
//                 console.error("Invalid file object:", csvFile);
//                 throw new Error("Invalid file object.");
//             }
//         } catch (error) {
//             console.error("Error uploading CSV file:", error);
//         }
//     };
    

//     return (
//         <Fragment>
//             <Modal show={show} onHide={handleClose}>
//                 <Modal.Header closeButton>
//                     <Modal.Title>Upload CSV</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     {/* Input field for uploading CSV file */}
//                     <input type="file" onChange={handleFileChange} />
//                 </Modal.Body>
//                 <Modal.Footer>
//                     <Button variant='secondary' onClick={handleClose}>
//                         Close
//                     </Button>
//                     <Button variant='primary' onClick={handleUploadClick}>
//                         Upload
//                     </Button>
//                 </Modal.Footer>
//             </Modal>
//         </Fragment>
//     );
// };

// export default CSVUploadModal;
